import Image from "next/image";
import React, { useEffect, useRef, useState } from 'react';
import styles from "./ShortsFeedCard.module.scss";
import { ShortsFeedItemData, ShortsContentType } from "@components/organisms/ShortsFeed/ShortsFeed.types";
import ArticleCard from "@components/organisms/ArticleCard/ArticleCard";
import Text from "@components/atoms/Text";
// import ReactPlayer from 'react-player'
import assetLoader from "@utils/internalImageLoder";
import { trackEventWithCommonProps, EventType } from "@utils/eventTracker";
import { PAGE_TYPES } from "@constants/pageTypes";
import { useIsMobile } from "@hooks/use-mobile";

interface ShortsFeedCardProps {
  item: ShortsFeedItemData;
  handleShare: (item: ShortsFeedItemData) => void;
  className?: string;
  isMuted: boolean;
  handleMuteToggle: () => void;
  isActive: boolean;
  isPrefetched?: boolean;
  getYouTubeEmbedUrl?: (url: string, muted: boolean, shouldAutoplay: boolean) => string | null;
  cityName?: string;
}

const ShortsFeedCard: React.FC<ShortsFeedCardProps> = ({ 
  item, 
  className, 
  handleShare, 
  isMuted, 
  handleMuteToggle, 
  isActive,
  isPrefetched = false,
  getYouTubeEmbedUrl: externalGetYouTubeEmbedUrl,
  cityName
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [youTubeKey, setYouTubeKey] = useState(0);
  const isMobile = useIsMobile();
  // const [useReactPlayer, setUseReactPlayer] = useState(true);

  const isArticle = item.type === ShortsContentType.ARTICLE;
  const isVideo = item.type === ShortsContentType.VIDEO;
  const isYouTubeVideo = isVideo && item?.sourceUrl?.includes('youtube.com');

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  useEffect(() => {
    setYouTubeKey(prev => prev + 1);
  }, [isMuted, isActive]);

  // useEffect(() => {
  //   setUseReactPlayer(true);
  // }, [item.id]);

  useEffect(() => {
    if (videoRef.current && !isYouTubeVideo) {
      videoRef.current.play().catch(error => {
        console.log('Autoplay prevented:', error);
      });
    }
  }, [isYouTubeVideo]);

  const getYouTubeVideoId = (url: string): string | null => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
    return match ? match[1] : null;
  };

  const getYouTubeEmbedUrl = (url: string, muted: boolean = true, shouldAutoplay: boolean = true): string | null => {
    const videoId = getYouTubeVideoId(url);
    const muteParam = muted ? '1' : '0';
    const autoplayParam = shouldAutoplay ? '1' : '0';
    return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=${autoplayParam}&mute=${muteParam}&loop=1&playlist=${videoId}&controls=0&rel=0&modestbranding=1&playsinline=1&showinfo=0&iv_load_policy=3&fs=0&disablekb=1&cc_load_policy=0` : null;
  };

  const getEmbedUrlFunction = externalGetYouTubeEmbedUrl || getYouTubeEmbedUrl;
  const embedUrl = isYouTubeVideo ? getEmbedUrlFunction(item.sourceUrl, isMuted, true) : null;

  useEffect(() => {
    if (isActive) {
      const track = async () => {
        await trackEventWithCommonProps("view", EventType.PAGE_EVENT, {
          pageType: PAGE_TYPES.HOME_PAGE,
          additionalProps: {
            view_type: "view_page_section",
            view_name: "view_homepage_section",
            section: "shorts_feed",
            feed_type: isArticle ? "article" : "video",
            feed_title: item.title,
            city_name: cityName,
          },
        });
      };
      track();
    }
  }, [isActive, item.id, item.title, isArticle, cityName]);

  useEffect(() => {
    const card = cardRef.current;
    if (!card || isMobile) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const newEvent = new WheelEvent('wheel', {
        deltaX: e.deltaX,
        deltaY: e.deltaY,
        deltaZ: e.deltaZ,
        deltaMode: e.deltaMode,
        clientX: e.clientX,
        clientY: e.clientY,
        screenX: e.screenX,
        screenY: e.screenY,
        bubbles: true,
        cancelable: true
      });
      
      const parentContainer = card.closest('.shortsFeedScrollContainer');
      if (parentContainer) {
        parentContainer.dispatchEvent(newEvent);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    card.addEventListener('wheel', handleWheel, { passive: false });
    card.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      card.removeEventListener('wheel', handleWheel);
      card.removeEventListener('touchmove', handleTouchMove);
    };
  }, [isMobile]);

  return (
    <div ref={cardRef} className={`${styles.shortsFeedItem} ${className}`}>
      {isArticle ? (
        <ArticleCard
          imageUrl={item.thumbnail}
          title={item.title}
          authorName={item.author}
          authorDate={item.publishedAt}
          description={item.description}
          readMoreLink={item.sourceUrl}
          readMoreText="Read More"
          cityName={cityName}
        />
      ) : isVideo ? (
                  <>
            {isYouTubeVideo && embedUrl ? (
              // <ReactPlayer 
              //   url={item.sourceUrl}
              //   playing={isActive}
              //   loop={true}
              //   controls={false}
              //   muted={isMuted}
              //   width="100%"
              //   height="100%"
              //   className={styles.videoPlayer}
              //   onError={(error) => {
              //     console.warn('ReactPlayer failed, falling back to iframe:', error);
              //     setUseReactPlayer(false);
              //   }}
              //   config={{
              //     youtube: {
              //       playerVars: {
              //         autoplay: isActive ? 1 : 0,
              //         controls: 0,
              //         rel: 0,
              //         modestbranding: 1,
              //         playsinline: 1,
              //         showinfo: 0,
              //         iv_load_policy: 3,
              //         fs: 0,
              //         disablekb: 1,
              //         cc_load_policy: 0
              //       }
              //     }
              //   }}
              // />
              
              <iframe
                key={youTubeKey}
                src={embedUrl}
                className={styles.videoPlayer}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={item.title}
              />
            ) : (
            <video
              ref={videoRef}
              src={item.sourceUrl}
              className={styles.videoPlayer}
              loop
              muted={isMuted}
              autoPlay={true}
              playsInline
              preload="metadata"
              poster={item.thumbnail}
            />
          )}
          <div className={styles.gradientOverlay}></div>

          <div className={styles.contentWrapper}>
            <div className={styles.middleSection}>
              <div className={styles.detailsContainer}>
                <h2 className={styles.title}>{item.title}</h2>
                <div className={styles.authorInfo}>
                  <div className={styles.authorText}>
                    {item.author && (
                      <span className={styles.authorName}>
                        {item.author}
                      </span>
                    )}
                    {/* <span className={styles.publishedDate}>
                      {formatDate(item.publishedAt)}
                    </span> */}
                  </div>
                </div>
              </div>
              {isActive && (
                <div className={styles.actionsContainer}>
                  <div className={styles.actionButton} onClick={handleMuteToggle}>
                    <Image
                      src={`${assetLoader()}${isMuted ? `/icons/mute-icon.svg` : `/icons/unmuteIcon.svg`}`}
                      alt={isMuted ? 'Mute' : 'Unmute'}
                      width={20}
                      height={20}
                      loading="lazy"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default ShortsFeedCard;